/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import Constants from '../../constants/constants'
import { EosTemplateMapDTO } from './api.eos'
import { toast } from '../toast'

const baseURL = '/api/settings'

export interface PoliciesDataType {
  allowTouchId?: boolean
  enforcePasswordUpdates?: boolean
  passwordUpdateFrequency?: number
  approverMustViewDocumentPriorToApproval?: boolean
  raUserMustViewDocumentPriorToApproval?: boolean
  requireESignatureForFileCommands?: boolean
  requireUsernameInESignature?: boolean
  openTaskReminderFrequency?: number
  startOpenTaskReminderAfter?: number
  stopOpenTaskReminderAfter?: number
  openTaskReminderNotificationFrequency?: number
  timeFormat24h?: boolean
  defaultTimeZoneId?: string
  defaultDateFormat?: string
  timeFormat?: string
  includeTimeZone?: boolean
  clientDateFormat?: string
  useReportsPassword?: boolean
  disableRequiredProperties?: boolean
  reportsPassword?: string | null
  reportTimeout?: number
  maxUnsuccessfulLoginAttempts?: number
  webSessionTimeout?: number
  allowUsersToManageChangeRequests?: boolean
  allowPermanentDelete?: boolean
  allowKeepVersionsFromCR?: boolean
  liveContentVerification?: boolean
  allowPersonalizeClientDateformat?: boolean
  clientTimeFormat24h?: boolean
  clientTimeFormatDelimiter?: string
  autoGenerateEtmfStudyStructures?: boolean
  allowBlindedUsersToGenerateReports?: boolean
  fastWebViewPdf?: boolean
  restrictSiteUserAccessToOwnedDocs?: boolean
  allowUserToDownloadWhenSourceCannotViewed?: boolean
  showEmailAddress?: boolean
  containsLegacyEOSStudy?: boolean
  forgeFontFallbackHandlingForWindows?: boolean
  pollingInterval?: number
  hideStackedDocumentVersion?: boolean
  activeLanguages?: string[]
  allowCompletionOfQCReviewTaskWithNoStatus?: boolean
  documentDueSoonInterval?: any
  enableDocumentDueSoonInterval?: any
}

interface JustificationReasonType {
  id?: number
  locale?: string
  position?: number
  text: string
  type?: string
}

interface JustificationReasonResponseType {
  elements: JustificationReasonType[]
}

interface FtpContextRequestType {
  id?: number | null
  host?: string
  name?: string
  port?: number
  implicit?: boolean
  remoteFolder?: string
  metadata?: UserType
  enabled?: boolean
  frequency?: number
  targetFolder?: number
  eosMediaTransfer?: boolean
  eosStudyGroupUuid?: string
  eosStudyUuid?: string
  eosStudyEnvUuid?: string
}

interface UserType {
  UserTypeuser?: string
  password?: string
}

interface CreateFtpContextRequestType {
  id?: number
  eosTemplateMapDTO?: EosTemplateMapDTO
  ftpContextRequestDTO?: FtpContextRequestType
}

export const allowedAuthTypes = () => {
  return baseApiService.fetch('/api/allowedauthtypes')
}

export const getAll = () => {
  return baseApiService.fetch(baseURL)
}

export const update = (data: PoliciesDataType) => {
  return baseApiService.fetch('/api/settings', {
    method: 'PUT',
    body: { ...data }
  })
}

export const getServerVersion = () => {
  return baseApiService.fetch('/version')
}

export const getTimezones = () => {
  return baseApiService.fetch(baseURL + '/timezones')
}

export const getJustifications = (lang: string) => {
  return baseApiService.fetch('/api/justifications', {
    params: {
      type: Constants.system.justificationTypes.JUSTIFICATION_CHANGE
    }
  })
}

export const getCrTeamTaskStepLabels = (lang?: string) => {
  return baseApiService.fetch('/api/v1/taskteamsteplabels')
}

export const getRejectionReasons = (lang?: string) => {
  const apiCall = baseApiService.fetch('/api/justifications', {
    params: {
      type: Constants.system.justificationTypes.JUSTIFICATION_REJECT
    }
  })
  apiCall.then((data) => {
    const dataLocal = data as JustificationReasonResponseType
    dataLocal?.elements?.sort((prev, next) => prev?.text?.toLowerCase().localeCompare(next?.text?.toLowerCase()))
  })
  return apiCall
}

export const exportSettingsData = (
  exportType: string,
  successCallback?: () => void,
  entityId?: number,
  translate?: any
) => {
  const params: { exportTypes: string; entityId?: number } = {
    exportTypes: exportType
  }

  if (entityId) {
    params.entityId = entityId
  }

  // Show info toast at the start
  toast.info(translate ? translate('BROWSER_DOWNLOAD_IS_PREPARING') : 'BROWSER_DOWNLOAD_IS_PREPARING')

  return baseApiService
    .fetch('/api/export', {
      params: { ...params }
    })
    .then(async (data) => {
      const typedData = data as { url?: string; fileSystemItemId?: number; fileName?: string }

      if (typedData?.fileSystemItemId) {
        try {
          // First, get the actual download URL from the file service
          const contentUrlResponse = await baseApiService.fetch(
            `/api/v1/files/${typedData.fileSystemItemId}/contenturl`
          )

          const downloadInfo = contentUrlResponse as { url?: string; fileName?: string }

          if (downloadInfo?.url) {
            // Now download the file using the provided URL
            const response = await fetch(downloadInfo.url, {
              credentials: 'include'
            })

            if (!response.ok) {
              throw new Error('Download failed')
            }

            // Get filename
            let filename = downloadInfo.fileName || typedData.url || 'export.json'

            // Ensure it has .json extension
            if (!filename.toLowerCase().endsWith('.json')) {
              filename = filename + '.json'
            }

            // Create blob and download
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            toast.success(
              translate ? translate('EXPORT_SETTINGS_SUCCESS_MSG', { name: filename }) : 'EXPORT_SETTINGS_SUCCESS_MSG'
            )

            if (typeof successCallback === 'function') {
              successCallback()
            }
          } else {
            toast.error('Export failed - no download URL returned')
          }
        } catch (error) {
          toast.error('Download failed')
          console.error('Download error:', error)
        }
      } else {
        toast.error('Export failed - no file ID returned')
      }
    })
    .catch((error) => {
      toast.error('Export failed')
      console.error('Export error:', error)
    })
}

export const importSettingsData = (
  file: File,
  importTypes: string[],
  successCallback?: (data: any) => void,
  errorCallback?: (error: any) => void
) => {
  const fd = new FormData()
  fd.append('file', file)

  const params = { importTypes: importTypes }

  return baseApiService
    .fetch('/api/import', {
      method: 'POST',
      params: params,
      body: fd
    })
    .then((data) => {
      if (typeof successCallback === 'function') {
        successCallback(data)
      }
      return data
    })
    .catch((error) => {
      if (typeof errorCallback === 'function') {
        errorCallback(error)
      }
      throw error
    })
}

export const getPerformanceView = () => {
  return baseApiService.fetch('/api/performanceview')
}

export const getPerformanceViewBgStatus = () => {
  return baseApiService.fetch('/api/performanceview/bgtasksstatus')
}

export const getActiveSessionsStatus = () => {
  return baseApiService.fetch('/api/performanceview/activeSessions')
}

export const isFtpNameUnique = (name: string) => {
  return baseApiService.fetch('/api/ftp/validate', {
    params: {
      name
    }
  })
}

export const getFtpContextsForPicker = (isEosMedia: boolean) => {
  return baseApiService.fetch('/api/ftp/ftpcontext', {
    params: {
      eosMedia: isEosMedia
    }
  })
}

export const getFtpContext = (id: number) => {
  if (!id) {
    toast.error('NO_FTP_ID')
    return
  }
  return baseApiService.fetch('/api/ftp/' + id)
}

export const updateFtpContext = (data: CreateFtpContextRequestType) => {
  if (!data.id) {
    return createFtpContext(data)
  } else {
    return baseApiService.fetch('/api/ftp/' + data.id, {
      method: 'PUT',
      body: {
        ...data
      }
    })
  }
}

export const createFtpContext = (data: CreateFtpContextRequestType) => {
  return baseApiService.fetch('/api/ftp', {
    method: 'POST',
    body: { ...data }
  })
}

export const getFtpContexts = () => {
  return baseApiService.fetch('/api/ftp/')
}

export const deleteFtpContext = (id: number) => {
  return baseApiService.fetch('/api/ftp/' + id, {
    method: 'DELETE'
  })
}

export const getPreUpgradeReports = () => {
  return baseApiService.fetch('/api/upgradevalidation/preupgradereports')
}

export const doPostUpgradeReport = (preUpgradeReportId: number) => {
  return baseApiService.fetch('/api/reports/postupgrade/' + preUpgradeReportId)
}

export const doPreUpgradeReport = () => {
  return baseApiService.fetch('/api/reports/preupgrade')
}

export const stopMaintenanceMode = () => {
  return baseApiService.fetch('/api/maintenancemode/stop')
}

export const startMaintenanceMode = () => {
  return baseApiService.fetch('/api/maintenancemode/start')
}

export const statusMaintenanceMode = () => {
  return baseApiService.fetch('/api/maintenancemode/status')
}

export const getEtmfFeature = () => {
  // HOW TO USE _.once() HERE
  return isFeatureEnabled('ETMF')
}

export const getRcrFeature = () => {
  // HOW TO USE _.once() HERE
  return isFeatureEnabled('RCR')
}

export const getEosFeature = () => {
  // HOW TO USE _.once() HERE
  return isFeatureEnabled('EOS')
}

export const getAwsS3Config = () => {
  // HOW TO USE _.once() HERE
  return baseApiService.fetch('/api/integrations/aws/storage/settings')
}

export const getAllFeatures = () => {
  // HOW TO USE _.once() HERE
  return baseApiService.fetch('/api/v1/features')
}

export const isFeatureEnabled = (feature: string) => {
  if (!['ETMF', 'RCR', 'EOS'].includes(feature)) {
    toast.error('Wrong feature provided')
    return
  }

  return baseApiService.fetch('/api/features/' + feature + '/enabled')
}

export const getDateFormats = () => {
  const formats = [
    { id: 'MM/dd/yyyy', value: 'DATE_FORMAT_MM/dd/yyyy' },
    { id: 'dd/MM/yyyy', value: 'DATE_FORMAT_dd/MM/yyyy' },
    { id: 'yyyy/MM/dd', value: 'DATE_FORMAT_yyyy/MM/dd' },
    { id: 'dd MMM, yyyy', value: 'DATE_FORMAT_dd MMM, yyyy' },
    { id: 'MMM dd, yyyy', value: 'DATE_FORMAT_MMM dd, yyyy' },
    { id: 'dd-MMM-yyyy', value: 'DATE_FORMAT_dd-MMM-yyyy' },
    { id: 'MMM-dd-yyyy', value: 'DATE_FORMAT_MMM-dd-yyyy' },
    { id: 'ddMMMyyyy', value: 'DATE_FORMAT_ddMMMyyyy' },
    { id: 'yyyyMMdd', value: 'DATE_FORMAT_yyyyMMdd' }
  ]
  return formats
}

export const getTimeFormats = () => {
  const formats = [
    { id: ':', value: 'TIME_FORMAT_:' },
    { id: '.', value: 'TIME_FORMAT_.' }
  ]
  return formats
}

export const getEmailToEtmf = () => {
  return baseApiService.fetch('/api/etmfemail/settings')
}

export const saveEmailToEtmf = (settings: string) => {
  // CHECK THE settings TYPE HERE
  return baseApiService.fetch('/api/etmfemail/settings', {
    method: 'PATCH',
    body: {
      settings
    }
  })
}

export const ftpSync = (id: number) => {
  return baseApiService.fetch('/api/ftp/sync/' + id)
}

export const validateFolderPath = (path: string, targetFolderId: number, ftpContextId?: number) => {
  return baseApiService.fetch('/api/ftp/validatefolderpath', {
    method: 'POST',
    body: {
      path: path,
      targetFolder: targetFolderId,
      ftpContextId: ftpContextId
    }
  })
}

export const revokeOauthEmailToEtmf = () => {
  return baseApiService.fetch('/api/etmfemail/oauth/revoke')
}

export const getAllEtmfThirdParties = () => {
  return baseApiService.fetch('/api/v1/thirdparty')
}

export const getThirdPartyById = (id: number) => {
  if (!id) {
    toast.error('Incorrect FTP ID')
    return
  }
  return baseApiService.fetch('/api/v1/thirdparty/' + id)
}

export const getTenantByUuid = (tenantUuid: string) => {
  return baseApiService.fetch(`/tenants/${tenantUuid}`)
}

export const appStatus = () => {
  return baseApiService.fetch('/status')
}

export const useSettingsApi = () => {
  return {
    allowedAuthTypes,
    appStatus,
    createFtpContext,
    deleteFtpContext,
    doPostUpgradeReport,
    doPreUpgradeReport,
    exportSettingsData,
    ftpSync,
    getActiveSessionsStatus,
    getAll,
    getAllEtmfThirdParties,
    getAllFeatures,
    getAwsS3Config,
    getCrTeamTaskStepLabels,
    getDateFormats,
    getEmailToEtmf,
    getEosFeature,
    getEtmfFeature,
    getFtpContext,
    getFtpContexts,
    getFtpContextsForPicker,
    getJustifications,
    getPerformanceView,
    getPerformanceViewBgStatus,
    getPreUpgradeReports,
    getRcrFeature,
    getRejectionReasons,
    getServerVersion,
    getTenantByUuid,
    getThirdPartyById,
    getTimeFormats,
    getTimezones,
    importSettingsData,
    isFeatureEnabled,
    isFtpNameUnique,
    revokeOauthEmailToEtmf,
    saveEmailToEtmf,
    startMaintenanceMode,
    statusMaintenanceMode,
    stopMaintenanceMode,
    update,
    updateFtpContext,
    useSettingsApi,
    validateFolderPath
  }
}
